import {
  Box,
  Center,
  HStack,
  Text,
  Image,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';

import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { CenteredSpinner, RoundedBadge } from '../../../components';
import {
  useGetProjectByIdQuery,
  useDeleteProjectMutation,
  useUpdateProjectMutation,
} from '../../../services';
import { DeleteProjectModal } from '../components';

import { ReactComponent as ChevronDownIcon } from '../../../assets/icons/chevron-down.svg';
import RedTrashImage from '../../../assets/icons/red-trash.svg';
import EditIconImage from '../../../assets/icons/edit.svg';
import { extractName } from '../../../utils';
import { getProjectType, projectStatusTypeMapper } from '../utils';
import { Project, ProjectStatus } from '../../../models';
import { useEffect, useState } from 'react';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const ProjectDetails = () => {
  const [projectStatus, setProjectStatus] =
    useState<Project['projectStatusEnum']>();
  const { projectId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useGetProjectByIdQuery(Number(projectId));

  const [updateProject, { isLoading: isUpdateProjectLoading }] =
    useUpdateProjectMutation();
  const [deleteProject, { isLoading: isDeleteProjectLoading }] =
    useDeleteProjectMutation();

  const {
    isOpen: isDeleteProjectModalOpen,
    onOpen: onOpenDeleteProjectModal,
    onClose: onCloseDeleteProjectModal,
  } = useDisclosure();

  const type = getProjectType();

  const handleUpdateProject = async () => {
    // The effect below also fires on mount, when no status has been chosen.
    // Without this guard the page PUTs `projectStatusEnum: undefined` on every
    // visit — harmless while the reply was ignored, but it would now raise an
    // error notification for a request the user never made.
    if (!projectStatus) return;

    const updatedProject = new FormData();

    updatedProject.set(
      'project',
      JSON.stringify({
        id: Number(projectId),
        projectStatusEnum: projectStatus,
      })
    );

    const response = await updateProject(updatedProject);

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not change the project status',
        description:
          'The project status is unchanged. Please try again in a moment.',
      });
    }

    // Cleared on both paths: it drives the menu button's spinner, so leaving it
    // set on failure would spin forever.
    setProjectStatus(undefined);
  };

  const handleDeleteProject = async () => {
    const response = await deleteProject({ projectId: Number(projectId) });

    // Was fire-and-hope: the page navigated to the project list even when the
    // delete was rejected, so a still-existing project looked deleted.
    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not delete this project',
        description: 'The project was not deleted. Please try again.',
      });
      return;
    }

    navigate(`/projects/${type}`);
  };

  useEffect(() => {
    handleUpdateProject();
  }, [projectStatus]);

  if (isLoading || !data) return <CenteredSpinner />;

  return (
    <Box mt="8">
      {isDeleteProjectModalOpen && (
        <DeleteProjectModal
          isOpen={isDeleteProjectModalOpen}
          onClose={isDeleted => {
            if (isDeleted) {
              void handleDeleteProject();
            }
            onCloseDeleteProjectModal();
          }}
        />
      )}

      <HStack px="2" my="2" justify="space-between">
        <HStack>
          <Box w="10" h="10" bg="custom_blue.1" borderRadius="full">
            {data.data.projectThumbnailImageURL ? (
              <Image
                src={data?.data.projectThumbnailImageURL}
                borderRadius="full"
              />
            ) : (
              <Center h="full" color="white" textTransform="capitalize">
                {extractName(data.data.projectName)}
              </Center>
            )}
          </Box>
          <Text fontWeight="bold" fontSize="xl">
            {data.data.projectName}
          </Text>
          <Text fontSize="lg">{data?.data.projectCode}</Text>
          <RoundedBadge
            colorSchema={data?.data.projectStatusEnum}
            textMapper={projectStatusTypeMapper}
          >
            {data?.data.projectStatusEnum}
          </RoundedBadge>
        </HStack>

        <Menu closeOnSelect={false}>
          {({ isOpen }) => (
            <>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                fontSize="sm"
                bg="none"
                _hover={{ bg: 'none' }}
                border="1px"
                borderColor="custom_gray.1"
              >
                Actions
              </MenuButton>
              <MenuList p="0">
                {/* TODO: reuse after implementation */}
                {/* <MenuItem
                  border="1px"
                  borderColor="custom_gray.4"
                  p="3"
                  onClick={() => handleUpdateProject('PAUSED')}
                >
                  <HStack>
                    <Image src={EditIconImage} alt="Pause project" />
                    <Text
                      color="custom_blue.1"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      Pause Project
                    </Text>
                  </HStack>
                </MenuItem> */}

                <MenuItem p="0">
                  <Button
                    w="full"
                    borderRadius="none"
                    border="1px"
                    borderColor="custom_gray.4"
                    px="3"
                    py="5"
                    justifyContent="start"
                    onClick={() =>
                      navigate(`/projects/${type}/${String(projectId)}`)
                    }
                    gap="3"
                    bg="white"
                  >
                    <Image src={EditIconImage} alt="Update project" />
                    <Text
                      color="custom_blue.1"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      Update Project
                    </Text>
                  </Button>
                </MenuItem>

                <MenuItem p="0">
                  <Button
                    w="full"
                    borderRadius="none"
                    border="1px"
                    borderColor="custom_gray.4"
                    px="3"
                    py="5"
                    justifyContent="start"
                    onClick={() => setProjectStatus(ProjectStatus.ACTIVE)}
                    isLoading={projectStatus === ProjectStatus.ACTIVE}
                    gap="3"
                    bg="white"
                  >
                    <Image src={EditIconImage} alt="Activate project" />
                    <Text
                      color="custom_blue.1"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      Activate Project
                    </Text>
                  </Button>
                </MenuItem>

                <MenuItem p="0">
                  <Button
                    w="full"
                    borderRadius="none"
                    border="1px"
                    borderColor="custom_gray.4"
                    px="3"
                    py="5"
                    justifyContent="start"
                    onClick={() => setProjectStatus(ProjectStatus.CANCELLED)}
                    isLoading={projectStatus === ProjectStatus.CANCELLED}
                    gap="3"
                    bg="white"
                  >
                    <Image src={EditIconImage} alt="Cancel project" />
                    <Text
                      color="custom_blue.1"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      Cancel Project
                    </Text>
                  </Button>
                </MenuItem>

                <MenuItem p="0">
                  <Button
                    w="full"
                    borderRadius="none"
                    border="1px"
                    borderColor="custom_gray.4"
                    px="3"
                    py="5"
                    justifyContent="start"
                    onClick={onOpenDeleteProjectModal}
                    isLoading={isDeleteProjectLoading}
                    gap="3"
                    bg="white"
                  >
                    <Image src={RedTrashImage} alt="Delete project" />
                    <Text color="error.600" fontSize="sm" fontWeight="medium">
                      Delete Project
                    </Text>
                  </Button>
                </MenuItem>
              </MenuList>
            </>
          )}
        </Menu>
      </HStack>

      {/* TODO: reuse after implementation */}
      {/* <Box
        borderRadius="md"
        boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
        border="1px"
        borderColor="custom_gray.3"
        p="4"
        mb="4"
      >
        <Heading color="custom_blue.1" fontSize="lg" fontWeight="bold" mb="2">
          Project Insights
        </Heading>
        <Text color="custom_blue.1" fontSize="md" mb="4">
          Provide a linear description below the title to give context to the
          user.
        </Text>

        <SimpleGrid columns={2} spacing={6}>
          <Box
            borderRadius="md"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            border="1px"
            borderColor="custom_gray.3"
            p="4"
          >
            <HStack mb="2">
              <HStack w="full" align="center">
                <Center
                  bg="custom_orange.3"
                  borderRadius="50"
                  w="10"
                  h="10"
                  outline="solid 8px"
                  outlineColor="custom_orange.4"
                >
                  <Icon as={TasksIcon} w="6" h="6" />
                </Center>
                <Text
                  color="custom_orange.2"
                  fontWeight="semibold"
                  fontSize="md"
                >
                  Your Today Tasks
                </Text>
              </HStack>
              <Icon as={DotsIcon} />
            </HStack>
            <Text color="custom_blue.1" fontSize="3xl" fontWeight="semibold">
              25/50 Tasks
            </Text>
          </Box>
          <Box
            borderRadius="md"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            border="1px"
            borderColor="custom_gray.3"
            p="4"
          >
            <HStack mb="2">
              <HStack w="full" align="center">
                <Center
                  bg="custom_red.2"
                  borderRadius="50"
                  w="10"
                  h="10"
                  outline="solid 8px"
                  outlineColor="custom_red.3"
                >
                  <Icon as={WarningIcon} w="6" h="6" />
                </Center>
                <Text
                  color="custom_orange.2"
                  fontWeight="semibold"
                  fontSize="md"
                >
                  Your At Risk Tasks
                </Text>
              </HStack>
              <Icon as={DotsIcon} />
            </HStack>
            <Text color="custom_blue.1" fontSize="3xl" fontWeight="semibold">
              5/50 Tasks
            </Text>
          </Box>
          <Box
            borderRadius="md"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            border="1px"
            borderColor="custom_gray.3"
            p="4"
          >
            <HStack mb="2">
              <HStack w="full" align="center">
                <Center
                  bg="custom_orange.3"
                  borderRadius="50"
                  w="10"
                  h="10"
                  outline="solid 8px"
                  outlineColor="custom_orange.4"
                >
                  <Icon as={TasksIcon} w="6" h="6" />
                </Center>
                <Text
                  color="custom_orange.2"
                  fontWeight="semibold"
                  fontSize="md"
                >
                  Your Today Tasks
                </Text>
              </HStack>
              <Icon as={DotsIcon} />
            </HStack>
            <Text color="custom_blue.1" fontSize="3xl" fontWeight="semibold">
              25/50 Tasks
            </Text>
          </Box>
          <Box
            borderRadius="md"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            border="1px"
            borderColor="custom_gray.3"
            p="4"
          >
            <HStack mb="2">
              <HStack w="full" align="center">
                <Center
                  bg="custom_red.2"
                  borderRadius="50"
                  w="10"
                  h="10"
                  outline="solid 8px"
                  outlineColor="custom_red.3"
                >
                  <Icon as={WarningIcon} w="6" h="6" />
                </Center>
                <Text
                  color="custom_orange.2"
                  fontWeight="semibold"
                  fontSize="md"
                >
                  Your At Risk Tasks
                </Text>
              </HStack>
              <Icon as={DotsIcon} />
            </HStack>
            <Text color="custom_blue.1" fontSize="3xl" fontWeight="semibold">
              5/50 Tasks
            </Text>
          </Box>
        </SimpleGrid>
      </Box> */}

      <Box
        bg="white"
        border="1px"
        borderColor="custom_gray.3"
        my="4"
        p="3"
        borderRadius="md"
        minH="80vh"
        h="auto"
      >
        <Outlet />
      </Box>
    </Box>
  );
};
